import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
  DocsTable,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_SCOPE,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';

const host = getConnectorHost('chatgpt');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'connect', title: '3. Add Chat.co in developer mode' },
  { id: 'approve', title: '4. Approve in Chat.co' },
  { id: 'first-answer', title: '5. Your first cited answer' },
  { id: 'credits', title: '6. Message credits' },
  { id: 'troubleshooting', title: '7. Common errors' },
  { id: 'disconnect', title: '8. Disconnect' },
  { id: 'developer-reference', title: '9. For developers & agents' },
  { id: 'references', title: '10. References' },
];

const stepLink = 'font-medium text-primary-700 underline';

const ChatGptConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in ChatGPT"
      description="Connect Chat.co to ChatGPT in developer mode with OAuth, choose a workspace, and get cited answers from your own Chat.co experts."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in ChatGPT | Chat.co Docs',
        description: 'Connect Chat.co to ChatGPT in developer mode and get cited answers.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['chatgpt', 'openai', 'developer mode', 'connector', 'mcp', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in ChatGPT"
      description="Add Chat.co as a custom connection in ChatGPT developer mode while the directory listing is in review."
      readingTime="8 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('chatgpt')}
      keyTakeaways={[
        'The ChatGPT directory listing is in review and not yet published.',
        'You can connect today in developer mode on the web, where your plan and workspace allow it.',
        'Authentication is OAuth: you sign in to Chat.co and choose one workspace.',
        'ChatGPT may ask you to confirm each expert question, because it saves a conversation and uses credits.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          Chat.co has been submitted to OpenAI for the ChatGPT directory and is in review. Until
          OpenAI publishes it, Chat.co does not appear when you browse ChatGPT&apos;s directory.
        </p>
        <p>
          The developer-mode connection below works now. We tested it end to end: listing experts, a
          cited answer, a follow-up, a direct search and opening a source.
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>ChatGPT on the web with developer mode.</strong> OpenAI lists developer mode for
            Plus, Pro, Business, Enterprise and Education accounts on the web. Availability can also
            depend on your workspace&apos;s policy, so a workspace admin may need to allow it.
          </li>
        </ConnectorPrerequisites>
        <DocsCallout variant="note" title="Labels change">
          OpenAI&apos;s current documentation calls these connections <strong>Plugins</strong>;
          older help articles say Connectors or Apps. If a label below looks different in your
          account, look for the equivalent setting.
        </DocsCallout>
      </section>

      <section id="connect" className="scroll-mt-28">
        <h2>3. Add Chat.co in developer mode</h2>
        <DocsSteps>
          <DocsStep title="Turn on developer mode">
            <p>
              In ChatGPT, open <strong>Settings → Security and login</strong> and switch on{' '}
              <strong>Developer mode</strong>. OpenAI marks developer mode as elevated risk. Only
              add servers you trust.
            </p>
          </DocsStep>
          <DocsStep title="Create a connection">
            <p>
              Go to{' '}
              <a
                href="https://chatgpt.com/plugins"
                target="_blank"
                rel="noopener noreferrer"
                className={stepLink}>
                chatgpt.com/plugins
              </a>{' '}
              and select the <strong>+</strong> button. Enter a name, such as{' '}
              <strong>Chat.co</strong>, and a short description, such as &ldquo;Ask my Chat.co
              experts for cited answers.&rdquo;
            </p>
          </DocsStep>
          <DocsStep title="Enter the server URL">
            <p>
              Under <strong>Connection</strong>, choose the public endpoint and enter the URL,
              including <code>/mcp</code>:
            </p>
            <DocsCodeBlock code={CHATCO_MCP_URL} language="url" />
          </DocsStep>
          <DocsStep title="Choose OAuth">
            <p>
              Set authentication to <strong>OAuth</strong>. If ChatGPT shows advanced OAuth
              settings, use the values below. Leave client ID and secret fields empty; Chat.co never
              needs an API key here.
            </p>
            <DocsTable
              headers={['Setting', 'Value']}
              rows={[
                ['Client registration', 'Dynamic client registration'],
                ['Scope', <code key="s">{CHATCO_MCP_SCOPE}</code>],
                ['OpenID Connect (OIDC)', 'Off'],
              ]}
            />
          </DocsStep>
          <DocsStep title="Create and sign in">
            <p>
              Create the connection. ChatGPT opens the Chat.co sign-in page; approve it as described
              in{' '}
              <a href="#approve" className={stepLink}>
                Approve in Chat.co
              </a>
              . Back in ChatGPT, review the discovered tools: <code>list_authorized_experts</code>,{' '}
              <code>consult_expert</code>, <code>search</code> and <code>fetch</code>.
            </p>
          </DocsStep>
          <DocsStep title="Use it in a conversation">
            <p>
              Start a new conversation, open the <strong>+</strong> menu, choose{' '}
              <strong>Developer mode</strong> and select Chat.co.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="note" title="Confirmation prompts are expected">
          Asking an expert saves a conversation in Chat.co and uses message credits, so ChatGPT may
          ask you to confirm the call and show its details first. Check the expert and question,
          then confirm.
        </DocsCallout>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>4. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>5. Your first cited answer</h2>
        <FirstRunWalkthrough where="a new ChatGPT conversation with Chat.co selected" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>6. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>7. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'There is no Developer mode setting',
              'Your plan or workspace policy does not allow it. Ask your ChatGPT workspace admin.',
            ],
            [
              'ChatGPT cannot create the connection',
              `Check that the URL is exactly ${CHATCO_MCP_URL}, authentication is OAuth, registration is dynamic and OIDC is off.`,
            ],
            [
              'ChatGPT answers without using Chat.co',
              'Select Chat.co from + → Developer mode in that conversation, or name it: “Use Chat.co to ask my experts…”.',
            ],
            [
              'Tools look out of date',
              'Open the connection at chatgpt.com/plugins and select Refresh, then start a new conversation.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>8. Disconnect</h2>
        <ChatcoDisconnect>
          <p>
            In ChatGPT, open{' '}
            <a
              href="https://chatgpt.com/plugins"
              target="_blank"
              rel="noopener noreferrer"
              className={stepLink}>
              chatgpt.com/plugins
            </a>
            , select the Chat.co connection and remove it.
          </p>
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>9. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>10. References</h2>
        <ConnectorReferences
          vendorLinks={[
            {
              label: 'OpenAI: ChatGPT developer mode',
              href: 'https://developers.openai.com/api/docs/guides/developer-mode',
            },
            {
              label: 'OpenAI: Connect and test your plugin',
              href: 'https://developers.openai.com/plugins/deploy/connect-chatgpt',
            },
            {
              label: 'OpenAI Help Center: Developer mode and MCP apps in ChatGPT',
              href: 'https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt',
            },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default ChatGptConnectorGuide;
